import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { DrawerForm } from '@dmc/ui';
import { RegisteredAssetBasicModel } from '@casops/store/apis/registeredAssetsApi';
import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AssetTracksForm.module.scss';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import { z } from 'zod';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import AssetTracksTable from '../table/AssetTracksTable';
import {
  AssetTrackSeed,
  getTrackRecordId,
  MAX_ASSET_TRACKS,
  setTracks,
} from '@casops/store/slices/assetTracksSlice';
import { DateTime } from 'luxon';
import { useStartDateTimeOptions, useStartDayOptions, useStartTimes } from './startTimes';
import { casOpsApi, QueryTag } from '@casops/store/api';

const MIN_DURATION = 1;
const MAX_DURATION = 120;

export interface AssetTracksFormModel {
  startDate: string | null;
  startTime: string | null;
  duration: number;
  assetIds: number[];
}

export const defaultAssetTracksFormValues: AssetTracksFormModel = {
  startDate: null,
  startTime: null,
  duration: 120,
  assetIds: [],
};

interface AssetTracksFormProps {
  assets: RegisteredAssetBasicModel[] | undefined;
}

export const AssetTracksForm = ({ assets }: AssetTracksFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const tracksCount = useAppSelector(state => state.assetTracks.trackIds.length);
  const formSelectionLimit = MAX_ASSET_TRACKS - tracksCount;

  // Create list of days and all start times.
  const { allStartTimes, uniqueStartDays } = useStartTimes();

  // Note: The API allows specifying an end date that is in the future,
  // so there is no need to restrict the duration based on other fields or current time.
  const schema = useMemo(() => {
    return z.object({
      startDate: z
        .string()
        .nullable()
        .refine(v => v !== null, { message: t('Validation.SpecifyStartDate') }),
      startTime: z
        .string()
        .nullable()
        .refine(v => v !== null, { message: t('Validation.SpecifyStartTime') }),
      duration: z
        .number()
        .min(MIN_DURATION, { message: t('Validation.DurationMin', { min: MIN_DURATION }) })
        .max(MAX_DURATION, { message: t('Validation.DurationMax', { max: MAX_DURATION }) })
        .int({ message: t('Validation.ValueMustBeInteger') }),
      assetIds: z.array(z.number()).max(formSelectionLimit, {
        message: t('Validation.AssetTrackLimitExceeded', { count: formSelectionLimit }),
      }),
    });
  }, [t, formSelectionLimit]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    watch,
  } = useForm<AssetTracksFormModel>({
    defaultValues: defaultAssetTracksFormValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
    delayError: 100, // Prevent validation states while the dropdowns switch to/from null values.
  });

  const canSubmit = !isSubmitting && isValid;

  const onSubmitInternal = useCallback(
    (data: AssetTracksFormModel) => {
      if (data.startTime !== null) {
        const startDateTime = DateTime.fromISO(data.startTime);
        const endDateTime = startDateTime.plus({ minutes: data.duration });
        const startTime = startDateTime.toUTC().toString();
        const endTime = endDateTime.toUTC().toString();

        const trackSeeds: AssetTrackSeed[] = data.assetIds.map(assetId => {
          const assetName = assets?.find(a => a.id === assetId)?.n ?? t('Unknown');

          return {
            assetId,
            assetName,
            startTime,
            endTime,
          };
        });

        // First, make sure that any existing tracks that have the exact same parameters are cleared from cache.
        // This can be accomplished by clearing the tags of the tracks that will be requested.
        // If a specific trackId is not already in cache, this will not affect anything.
        const trackTagsToInvalidate = trackSeeds.map(seed => ({
          type: QueryTag.AssetTrack,
          id: getTrackRecordId(seed),
        }));
        dispatch(casOpsApi.util.invalidateTags(trackTagsToInvalidate));

        // Save the track request details to the store, which will trigger data fetching from other components.
        dispatch(setTracks(trackSeeds));
        dispatch(closeSidePanel());
      }
    },
    [t, dispatch, assets],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  const startDayOptions = useStartDayOptions(uniqueStartDays);

  const selectedDay = watch('startDate');

  const { field } = useController({ control, name: 'startTime' });

  useEffect(() => {
    if (selectedDay !== null) {
      field.onBlur();
      field.onChange(null);
    }
    // Intentionally exclude 'field' from the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  const filteredStartTimes =
    selectedDay !== null ? allStartTimes.filter(([startDay]) => selectedDay === startDay) : [];

  const startDateTimeOptions = useStartDateTimeOptions(filteredStartTimes);

  return (
    <DrawerForm
      fieldsClassName={styles.formContent}
      footer={
        <VdsButton
          variant="primary"
          size="medium"
          disabled={!canSubmit}
          isFullWidth
          onClick={onSubmit}
        >
          {t('ApplyAssetTracks')}
        </VdsButton>
      }
    >
      <VdsNotification variant="inline" status="informative" showCloseButton={false}>
        <span slot="header" style={{ paddingRight: 'var(--spacing-03)' }}>
          {t('DataQueryNotification.Title')}
        </span>
        <span slot="body">{t('DataQueryNotification.Message')}</span>
      </VdsNotification>
      <div className={styles.timeFields}>
        <CasFormDropdown
          control={control}
          name="startDate"
          label={t('Fields.StartDate')}
          items={startDayOptions}
        />
        <CasFormDropdown
          control={control}
          name="startTime"
          label={t('Fields.StartTime')}
          items={startDateTimeOptions}
          disabled={selectedDay === null || startDateTimeOptions.length === 0}
        />
        <CasFormInput
          control={control}
          name="duration"
          label={t('Fields.Duration')}
          helperText={t('Validation.DurationMax', { max: MAX_DURATION })}
          type="number"
        />
      </div>
      <AssetTracksTable control={control} assets={assets} />
    </DrawerForm>
  );
};

export default AssetTracksForm;
